/**
 * Passphrase hashing for the web screen-lock feature.
 *
 * Threat model:
 *
 *   - The passphrase itself is never stored. We persist (salt, hash, algorithm)
 *     only, so a forensic dump of IndexedDB cannot reveal the passphrase.
 *   - The hash is salted (32 bytes per install) so two users with the same
 *     passphrase don't share a hash, defeating precomputed rainbow tables.
 *   - Verification is constant-time: SubtleCrypto.verify() compares two
 *     HMAC tags over fixed-length hash buffers, so timing cannot leak how
 *     many leading bytes matched.
 *
 * KDF choice:
 *
 *   Web SubtleCrypto ships PBKDF2 natively. We use PBKDF2-SHA256 at
 *   600 000 iterations (OWASP 2023 baseline). The output is fixed at 32 bytes
 *   regardless of future algorithm swap so the on-disk shape is forward-
 *   compatible. An `algorithm` discriminator field on disk supports a future
 *   Argon2id-via-WASM migration without breaking existing records.
 *
 *   iOS counterpart (`AppLockPin.swift`) uses HKDF-SHA256 iterated 16 384
 *   times — that was the best available iOS primitive. On web we can and
 *   should use PBKDF2 at the OWASP-recommended cost.
 *
 * Web adaptations vs iOS:
 *   - PBKDF2-SHA256 600k iterations instead of HKDF-SHA256 16k rounds.
 *   - SubtleCrypto.verify(HMAC) for constant-time comparison instead of a
 *     hand-rolled byte loop.
 *   - No `pinNotNumericOnly` error: the web passphrase field accepts arbitrary
 *     Unicode (6–64 chars), not digits-only. This matches the task spec
 *     ("passphrase 6–64 chars").
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AppLockPin.swift`.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Only algorithm currently produced. The discriminator allows future swap. */
export type PassphraseHashAlgorithm = "PBKDF2-SHA256";

export interface AppLockPassphraseHash {
  /** 32 random bytes, generated once per passphrase set. Never reused. */
  readonly salt: Uint8Array;
  /**
   * 32 bytes of derived hash. The PBKDF2 output used as an HMAC key to
   * produce the stored tag — see `hashPassphrase` and `verifyPassphrase`
   * for the two-step construction that enables constant-time comparison via
   * SubtleCrypto.verify().
   */
  readonly hash: Uint8Array;
  /** ISO-8601 string. When this hash was written. Auditing only. */
  readonly createdAt: string;
  /** Discriminator so a future Argon2id/scrypt WASM swap can coexist. */
  readonly algorithm: PassphraseHashAlgorithm;
  /** PBKDF2 iteration count embedded so the verifier doesn't have to guess. */
  readonly iterations: number;
}

/** Parameters accepted by `hashPassphrase`. */
export interface PassphraseHashParams {
  algorithm: PassphraseHashAlgorithm;
  iterations: number;
}

/** Default production params. OWASP 2023 PBKDF2-SHA256 baseline. */
export const DEFAULT_PARAMS: PassphraseHashParams = {
  algorithm: "PBKDF2-SHA256",
  iterations: 600_000,
};

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class PassphraseTooShortError extends Error {
  constructor(readonly length: number) {
    super(`Passphrase too short: ${length} chars (minimum ${MIN_PASSPHRASE_LENGTH})`);
    this.name = "PassphraseTooShortError";
  }
}

export class PassphraseTooLongError extends Error {
  constructor(readonly length: number) {
    super(`Passphrase too long: ${length} chars (maximum ${MAX_PASSPHRASE_LENGTH})`);
    this.name = "PassphraseTooLongError";
  }
}

export class UnsupportedAlgorithmError extends Error {
  constructor(readonly algorithm: string) {
    super(`Unsupported passphrase hash algorithm: ${algorithm}`);
    this.name = "UnsupportedAlgorithmError";
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MIN_PASSPHRASE_LENGTH = 6;
export const MAX_PASSPHRASE_LENGTH = 64;

const SALT_BYTES = 32;
const OUT_BYTES = 32;

/**
 * Fixed info string used as the HMAC message when producing the stored tag
 * from the raw PBKDF2 output. This separates the KDF output from the
 * comparison tag so a subkey leak doesn't immediately reveal the raw PBKDF2
 * output.
 */
const HMAC_INFO = new TextEncoder().encode("kaether.applock.v1");

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Hash a passphrase and return a persistable AppLockPassphraseHash.
 *
 * The caller is expected to have already generated the salt via
 * `generateSalt()`.  We re-validate length defensively.
 *
 * @throws PassphraseTooShortError  if passphrase.length < 6
 * @throws PassphraseTooLongError   if passphrase.length > 64
 */
export async function hashPassphrase(
  passphrase: string,
  salt: Uint8Array,
  params: PassphraseHashParams = DEFAULT_PARAMS,
  now: Date = new Date(),
): Promise<AppLockPassphraseHash> {
  validateLength(passphrase);

  if (params.algorithm !== "PBKDF2-SHA256") {
    throw new UnsupportedAlgorithmError(params.algorithm);
  }

  const raw = await deriveRaw(passphrase, salt, params.iterations);
  const tag = await pbkdf2HashToTag(raw);

  return {
    salt,
    hash: tag,
    createdAt: now.toISOString(),
    algorithm: "PBKDF2-SHA256",
    iterations: params.iterations,
  };
}

/**
 * Constant-time verification.
 *
 * We always run the full KDF before the comparison so a too-short passphrase
 * costs the same as a wrong-length-but-plausible one. The comparison itself
 * uses `SubtleCrypto.verify(HMAC)` which is required by the spec to be
 * constant-time.
 *
 * Returns `false` on any mismatch — never throws on a wrong passphrase.
 *
 * @throws UnsupportedAlgorithmError  if `expected.algorithm` is not recognised
 */
export async function verifyPassphrase(
  passphrase: string,
  expected: AppLockPassphraseHash,
): Promise<boolean> {
  if (expected.algorithm !== "PBKDF2-SHA256") {
    throw new UnsupportedAlgorithmError(expected.algorithm);
  }

  // Validate length AFTER branching on algorithm (so algorithm errors surface
  // clearly), but run the full KDF even for out-of-range lengths so timing
  // is uniform. We clamp to the valid range for the KDF call to avoid
  // pathological runtimes while keeping the comparison.
  const tooShort = passphrase.length < MIN_PASSPHRASE_LENGTH;
  const tooLong  = passphrase.length > MAX_PASSPHRASE_LENGTH;
  const kdfInput = tooLong ? passphrase.slice(0, MAX_PASSPHRASE_LENGTH) : passphrase;

  const raw = await deriveRaw(kdfInput, expected.salt, expected.iterations);
  const candidate = await pbkdf2HashToTag(raw);

  if (tooShort || tooLong) {
    // Run the comparison anyway (constant-time) then discard.
    await constantTimeEqual(candidate, expected.hash);
    return false;
  }

  return constantTimeEqual(candidate, expected.hash);
}

/**
 * Generate 32 cryptographically-random salt bytes via `crypto.getRandomValues`.
 * A new salt is generated on every `setPassphrase` call — never reused.
 */
export function generateSalt(): Uint8Array {
  const bytes = new Uint8Array(SALT_BYTES);
  crypto.getRandomValues(bytes);
  return bytes;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Validate passphrase character length (Unicode code-points via spread). */
function validateLength(passphrase: string): void {
  // Use [...passphrase].length so multi-scalar emoji count as one char.
  const len = [...passphrase].length;
  if (len < MIN_PASSPHRASE_LENGTH) throw new PassphraseTooShortError(len);
  if (len > MAX_PASSPHRASE_LENGTH) throw new PassphraseTooLongError(len);
}

/**
 * Run PBKDF2-SHA256 and return `OUT_BYTES` raw bytes.
 * This is the expensive step; everything else is fast.
 */
async function deriveRaw(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations,
    },
    baseKey,
    OUT_BYTES * 8,
  );
  return new Uint8Array(bits);
}

/**
 * Convert raw PBKDF2 output into a stored tag via HMAC-SHA256.
 *
 * Using HMAC lets `SubtleCrypto.verify()` do the constant-time comparison
 * without rolling our own byte loop. The HMAC message is the fixed
 * `HMAC_INFO` string; the HMAC key is the raw PBKDF2 bytes.
 *
 * Result: 32-byte HMAC tag.
 */
async function pbkdf2HashToTag(raw: Uint8Array): Promise<Uint8Array> {
  const hmacKey = await crypto.subtle.importKey(
    "raw",
    raw,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", hmacKey, HMAC_INFO);
  return new Uint8Array(sig);
}

/**
 * Constant-time equality check via SubtleCrypto.verify(HMAC).
 *
 * We import `b` as the HMAC key and verify `a` as the "signature" over the
 * same fixed message. The spec mandates that `verify` is constant-time.
 * Returns true iff the two 32-byte tags are identical.
 *
 * If the arrays differ in length, returns false immediately (both are always
 * 32 bytes in practice; a different length would be a programming error, not
 * a timing-sensitive comparison).
 */
async function constantTimeEqual(a: Uint8Array, b: Uint8Array): Promise<boolean> {
  if (a.length !== b.length) return false;
  // Re-use HMAC to get the CT guarantee: import `b` as HMAC key, sign the
  // fixed HMAC_INFO to get `tagB`, then verify `a` against `tagB`.
  // Equivalent: both sides derive their HMAC tag from HMAC_INFO; we compare
  // those tags using SubtleCrypto.verify which is required to be CT.
  const keyA = await crypto.subtle.importKey(
    "raw", a, { name: "HMAC", hash: "SHA-256" }, false, ["sign"],
  );
  const sigA = await crypto.subtle.sign("HMAC", keyA, HMAC_INFO);
  // Compare via CT verify: import `b` as a verify-key and ask SubtleCrypto
  // whether sigA is a valid HMAC of HMAC_INFO under key `b`. True iff
  // HMAC(b, HMAC_INFO) === sigA === HMAC(a, HMAC_INFO), i.e. a === b.
  // SubtleCrypto.verify is required by spec to be constant-time.
  const compKey = await crypto.subtle.importKey(
    "raw", b, { name: "HMAC", hash: "SHA-256" }, false, ["verify"],
  );
  return crypto.subtle.verify("HMAC", compKey, sigA, HMAC_INFO);
}
